<template>
    <div ref="outerElementRef" class="parent-control-settings" >
        <div v-if="!adultPincode" class="parent-control-settings-relogin" :class="{'is-focused':isFocused}">
            <div class="parent-control-settings__title">{{ reloginTitle }}</div>
            <button 
                ref="button" 
                class="btn parent-control-settings__button" 
                @click="signOut"
                @focus="onFocusBtn"
                @blur="onBlurBtn"
            >
                {{ btnSignOut }}
            </button>
            <custom-modal
                v-if="isShownDialogModal"
                :title="dialogModal.title"
                :message="dialogModal.message"
                :buttons="dialogModal.buttons"
                :name="dialogModal.name"
                @on-btn-click="onModalClick"
            />
        </div>

        <div v-else>
            <div class="parent-control-settings__title">{{ ratingsTitle }}</div>

            <div class="parent-control-settings__wrapper">
                <button
                    v-for="(rating,index) in ratings" 
                    :id="getId(index, ParentControlOptionType.Rating)" 
                    :key="index"
                    :data-keyup="getKeyUpId(ParentControlOptionType.Rating, index)"
                    :data-keydown="getKeyDownId(ParentControlOptionType.Rating, index)"
                    class="parent-control-settings__option parent-control-settings__option-rating"
                    :class="{'parent-control-settings__option_active':isActive(rating)}"
                    @click="onClickRating(rating)"
                    @focus="onFocus(rating)"
                    @blur="onBlurBtn"
                >
                    <span
                        class="parent-control-settings__radio"
                        :class="{'parent-control-settings__radio_active':isChecked(rating)}"
                    />
                    <span class="parent-control-settings__text">{{ rating.title }}</span>
                </button>
            </div>

            <div v-if="!checkedNoRestrictions" class="parent-control-settings__title">{{ unlockTimesTitle }}</div>
            
            <div v-if="!checkedNoRestrictions" class="parent-control-settings__wrapper">
                <button 
                    v-for="(times,index) in unlockTimes" 
                    :id="getId(index, ParentControlOptionType.Times)"
                    :key="index" 
                    :data-keyup="getKeyUpId(ParentControlOptionType.Times, index)"
                    :data-keydown="getKeyDownId(ParentControlOptionType.Times, index)"
                    class="parent-control-settings__option"
                    :class="{'parent-control-settings__option_active':isActive(times)}"
                    @click="onClickTime(times)"
                    @focus="onFocus(times)"
                    @blur="onBlurBtn"
                >
                    <span
                        class="parent-control-settings__radio"
                        :class="{'parent-control-settings__radio_active':isChecked(times)}"
                    />
                    <span class="parent-control-settings__text">{{ times.title }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { useNuxtApp } from '#app';
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { Strings } from "@/locales/types/enums";
    import { useApplicationStore } from "@/stores/Application";
    import type { ICustomModalState } from '@/composables/globalModals/types'
    import { useI18n } from "vue-i18n";
    import { useParentControl } from "@/composables/parentControl/useParentControl"
    import { useFocusedComponents } from '@/composables/useFocusedComponents';
    import { type Ref, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
    import { ParentControlOptionType, type IRating, type ITime } from "@/composables/parentControl/types";
    import { useAccountStore } from "@/stores/Account";
    import { storeToRefs } from 'pinia';
    import { useParentControlStore } from "@/stores/ParentControl";
    import { onBeforeRouteLeave, type RouteLocationNormalizedGeneric } from 'vue-router';

    const ApplicationStore = useApplicationStore();
    const {
        getVisibleForgot: isForgotVisible,
    } = storeToRefs(ApplicationStore);
    const {
        deleteSelectedIdProgram,
    } = ApplicationStore;

    const AccountStoreModule = useAccountStore();
    const {
        Logout,
    } = AccountStoreModule;
    const ParentControlStoreModule = useParentControlStore();
    const {
        SetIncorrectAdultPin,
        ShowParentModal,
        HideParentModal
    } = ParentControlStoreModule;
    const {
        getIsModalParentVisible: isTypeModeEnabledPin,
        getIsAdultPinCorrect: isAdultPinCorrect,
    } = storeToRefs(ParentControlStoreModule);

    const router = useRouter();
    const emit = defineEmits(['data-loading']);
    const { $domains, $navigator } = useNuxtApp();
    const { t } = useI18n();
    const { getCurrentRating, ratings, unlockTimes, getCurrentTimes, setCurrentRating, setCurrentTimes } = useParentControl();
    const { setFocused, unsetFocused, outerElementRef } = useFocusedComponents('ParentControlPage');
    const checkedRating = getCurrentRating();
    const checkedTimes = getCurrentTimes();
    const dialogModal: ICustomModalState = {
        title: "",
        message: t(Strings.SETTINGS_ACCOUNT_MODAL_TXT).toString(),
        buttons: [
            t(Strings.SETTINGS_ACCOUNT_MODAL_NEGATIVE).toString(),
            t(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE).toString()
        ],
        name: "parents",
        focusedInd: 1,
    };
    const adultPincode = $domains.lsCacher.has('AdultPin');
    const reloginTitle: string = t(Strings.SETTINGS_PARENT_CONTROL_RELOGIN_TITLE).toString();
    const btnSignOut: string = t(Strings.SETTINGS_ACCOUNT_BTN_SIGN_OUT).toString();
    const ratingsTitle: string = t(Strings.SETTINGS_PARENT_CONTROL_RATINGS_RATING).toString();
    const unlockTimesTitle: string = t(Strings.SETTINGS_PARENT_CONTROL_UNLOCK_TIMES_UNLOCK_TIME).toString();

    const focusedItem: Ref<IRating | ITime | null> = ref(null);
    const selectedRating: Ref<IRating | null> = ref(null);
    const selectedTimes: Ref<ITime | null> = ref(null);
    const toPath: Ref<RouteLocationNormalizedGeneric | null> = ref(null);
    const confirmedPin: Ref<boolean> = ref(false);
    const beforeSelectedRating: Ref<IRating | null> = ref(null);
    const beforeSelectedTimes: Ref<ITime | null> = ref(null);
    const activeIndex: Ref<number> = ref(0);   
    const isFocused: Ref<boolean> = ref(false);
    const isShownDialogModal: Ref<boolean> = ref(false);
    const button: Ref<HTMLButtonElement | null> = ref(null);

    const checkedNoRestrictions = computed((): boolean => {
        if (selectedRating.value?.title === t(Strings.SETTINGS_PARENT_CONTROL_RATINGS_NO_RESTRICTION).toString()) {
            return true;
        }
        return false;
    });

    const isEnabledBack = computed((): boolean => {
            return (
                beforeSelectedTimes.value?.title === selectedTimes.value?.title 
                && beforeSelectedRating.value?.title === selectedRating.value?.title  
                || confirmedPin.value 
                || beforeSelectedRating.value?.title === ratings[0].title 
                && selectedRating.value?.title ===  ratings[0].title);
        });

    const onClickRating = (item: IRating) => {
        selectedRating.value = item;
        activeIndex.value = ratings.indexOf(item);
    };
 
    const onClickTime = (item: ITime) => {
        selectedTimes.value = item;
        activeIndex.value = unlockTimes.indexOf(item);
    };
    const onFocus = (item: IRating | ITime) => {
        focusedItem.value = item;
        registerKeyListeners();
        isFocused.value = true;
    };
    const onFocusBtn = () => {
        registerKeyListeners();
        isFocused.value = false;
    };
    const onBlurBtn = () => {
        unregisterKeyListeners();
    };

    const signOut = async (): Promise<void> => {
        isShownDialogModal.value = true;
    };

    const onModalClick = async (answer: string) => {
        isShownDialogModal.value = false;
        if (answer === t(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE)) {
            emit("data-loading", true);
            await Logout();
            await router.push(linkItems.login.routerPath);
            emit("data-loading", false);
        } else {
            if (button.value) {
                button.value.focus();
            }
        }
    };

    const isChecked = (item: IRating | ITime): boolean => {
        if (selectedRating.value?.title == item.title || selectedTimes.value?.title == item.title) {
            return true;
        }
        return false;
        
    };
    const isActive = (item: IRating | ITime): boolean => {
        if (focusedItem.value?.title == item.title) {
            return true;
        }
        return false;
    };
    const getId = (index: number,type: ParentControlOptionType): string => {
        if (type === ParentControlOptionType.Rating) {
            return `${ParentControlOptionType.Rating}${index}`;
        }
        return `${ParentControlOptionType.Times}${index}`;
    };

    const getKeyDownId = (type: ParentControlOptionType, index: number): string => {
        if (type === ParentControlOptionType.Rating) {
            if (index < ratings.length - 1) {
                return getId(index + 1, ParentControlOptionType.Rating);
            }
            if (checkedNoRestrictions.value) {
                return getId(index, ParentControlOptionType.Rating);
            }
            return getId(0, ParentControlOptionType.Times);
        }
        if (index < unlockTimes.length - 1) {
            return getId(index + 1, ParentControlOptionType.Times);
        }
        return getId(index, ParentControlOptionType.Times);
    };

    const getKeyUpId = (type: ParentControlOptionType, index: number): string => {
        if (type === ParentControlOptionType.Rating) {
            if (index > 0) {
                return getId(index - 1, ParentControlOptionType.Rating);
            }
            return getId(index, ParentControlOptionType.Rating);
        }
        if (index > 0) {
            return getId(index - 1, ParentControlOptionType.Times);
        }
        return getId(ratings.length - 1, ParentControlOptionType.Rating);
    };

        const getFocusedItemId = (item: IRating | ITime | null) => {
            const indexRatings = ratings.findIndex((i: IRating) => i.title === item?.title);
            const indexTimes = unlockTimes.findIndex((i: ITime) => i.title === item?.title);
            if (indexRatings >= 0) {
                return getId(indexRatings, ParentControlOptionType.Rating);
            } 
            if (indexTimes >= 0) {
                return getId(indexTimes, ParentControlOptionType.Times);
            }
            return getId(0, ParentControlOptionType.Rating)
        };
        const setSelfFocused = () => {
            if (!adultPincode) {
                if (button.value) {
                    button.value.focus();
                }
            } else {
                const focusedItemId = getFocusedItemId(focusedItem.value); 
                document.getElementById(focusedItemId)?.focus();
            }

            setFocused();
        };

        const unsetSelfFocused = (): void => {
            unsetFocused();
        };
        const correctedPin = () => {
            HideParentModal();
            setCurrentRating(selectedRating.value?.id);
            setCurrentTimes(selectedTimes.value?.type);
            SetIncorrectAdultPin();
            confirmedPin.value = true;
        };
        const registerKeyListeners = () => {
            $navigator.on("keypress:left", onKeyLeft);
            $navigator.on("keypress:back", onKeyBack);
        };
        const unregisterKeyListeners = () => {
            $navigator.off("keypress:left", onKeyLeft);
            $navigator.off("keypress:back", onKeyBack);
        };

        const onKeyLeft = (event: KeyboardEvent): void => {
            const element = event.target as HTMLElement;
            if (element.getAttribute("data-type") === "modal-button") {
                return;
            }
            goToBack();
        };
        const onKeyBack = (): void => {
            if (!isForgotVisible.value) {
                goToBack();
            }
        };
        const goToBack = (): void => {
           void router.push(linkItems.settings.routerPath);
        };


        watch(confirmedPin, (value: boolean) => {
            if (value && toPath.value !== null) {
                void router.push(toPath.value.fullPath);
            }
        });
        watch(isAdultPinCorrect, (value: boolean) => {
            if (value) {
                correctedPin();
            }
        });
        watch(isTypeModeEnabledPin, (value: boolean) => {
            if (!value) {
                const id = getFocusedItemId(focusedItem.value);
                document.getElementById(id)?.focus();
            }
        });


    onBeforeUnmount(() => {
        unsetSelfFocused();
    });

    onBeforeRouteLeave((to, _from, next) => {
        toPath.value = to;
        if (isEnabledBack.value) {
            HideParentModal();
            deleteSelectedIdProgram();

            next();
        } else {
            ShowParentModal();
        }
    });

    onMounted(() => {
        setSelfFocused();

        emit("data-loading", false);
  
        selectedTimes.value = checkedTimes ?? null;
        selectedRating.value = checkedRating ?? null;
        beforeSelectedRating.value = checkedRating ?? null;
        beforeSelectedTimes.value = checkedTimes ?? null;
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
